/*#
# segmented select

This is a fairly general-purpose segmented select control.

```html
<div class="grid">
  <tosi-segmented value="yes" choices="yes, no, don't care">
    Should we?
  </tosi-segmented>

  <div>
    <b>Localized!</b><br>
    <tosi-segmented
      localized
      title="do you like?"
      choices="yes=Yes:thumbsUp, no=No:thumbsDown"
    ></tosi-segmented>
  </div>

  <tosi-segmented
    style="--segmented-direction: column; --segmented-align-items: stretch"
    choices="in a relationship, single"
    other="it's complicated..."
    placeholder="please elaborate"
    value="separated"
  >
    Relationship Status
  </tosi-segmented>

  <tosi-segmented
    multiple
    style="--segmented-direction: column; --segmented-align-items: start; --segmented-option-grid-columns: 24px 24px 100px; --segmented-input-visibility: visible;"
    choices="star=Star:star, game=Game:game, bug=Bug:bug, camera=Camera:camera"
    value="star,bug"
  >
    Pick all that apply
  </tosi-segmented>
</div>
```
```css
.preview .grid {
  --segmented-option-current-background: var(--brand-color);
  --segmented-option-current-color: var(--brand-text-color);
  padding: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
```
```js
function logEvent(event) {
  const { target } = event
  if (target.matches('tosi-segmented')) {
    console.log((target.textContent || target.title).trim(), target.value)
  }
}
preview.addEventListener('change', logEvent, true)
```
```test
const segmented = preview.querySelectorAll('tosi-segmented')
test('segmented controls render', () => {
  expect(segmented.length).toBe(4)
})
test('first segmented has value "yes"', () => {
  expect(segmented[0].value).toBe('yes')
})
test('multiple segmented has array value', () => {
  expect(segmented[3].value).toBe('star,bug')
})
```

> Check the console to see the values being set.

## Form Integration

`<tosi-segmented>` is form-associated, meaning it works directly in native `<form>` elements:

```html
<form class="segmented-form">
  <tosi-segmented name="choice" choices="a,b,c" required></tosi-segmented>
  <button type="submit">Submit</button>
  <span class="output"></span>
</form>
```
```js
const form = preview.querySelector('.segmented-form')
form.addEventListener('submit', (e) => {
  e.preventDefault()
  const data = new FormData(form)
  form.querySelector('.output').textContent = 'Submitted: ' + data.get('choice')
})
```

## Properties

- `values` is an array of values (only really useful if `multiple` is set to true)

You can set `choices` programmatically to an array of `Choice` objects:

    interface Choice {
      icon?: string | IconElement
      value: string
      caption: string
    }

## Attributes

- `choices` is a string of comma-delimited options of the form `value=caption:icon`,
  where caption and icon are optional
- `multiple` allows multiple selection
- `name` the form field name (for formAssociated support)
- `other` (default '', meaning other is not allowed) is the caption for other options, allowing
  the user to input their choice. It will be reset to '' if `multiple` is set.
- `placeholder` is the placeholder displayed in the `<input>` field for **other** responses
- `localized` automatically localizes captions
- `required` marks the field as required for form validation

## Styling

The following CSS variables can be used to control customize the `<tosi-segmented>` component.

    --segmented-align-items
    --segmented-direction
    --segmented-option-color
    --segmented-option-current-background
    --segmented-option-current-color
    --segmented-option-font
    --segmented-option-gap
    --segmented-option-grid-columns
    --segmented-option-icon-color
    --segmented-option-padding
    --segmented-options-background
    --segmented-options-border-radius
    --segmented-placeholder-opacity
*/

/*{ "parent": "Form Components", "description": "<tosi-segmented> is a form-associated segmented control for choosing one value or several, with icons and labels." }*/

import {
  ElementCreator,
  elements,
  varDefault,
  deprecated,
  withAttributes,
} from 'tosijs'
import { icons } from './icons.js'
import type { IconElement } from './icon-types.js'
import { tosiLocalized } from './localize.js'
import { splitCaptionIcon } from './caption-icon.js'

const { div, slot, label, span, input } = elements

interface Choice {
  icon?: string | IconElement
  value: string
  caption: string
}

interface SegmentParts {
  [key: string]: HTMLElement
  custom: HTMLInputElement
  options: HTMLElement
}

export class TosiSegmented extends withAttributes({
  direction: 'row',
  other: '',
  multiple: false,
  name: '',
  placeholder: 'Please specify…',
  localized: false,
  required: false,
}) {
  static preferredTagName = 'tosi-segmented'
  static formAssociated = true

  private _choices: Choice[] = []

  get choices(): Choice[] {
    return this._choices
  }

  set choices(v: Choice[] | string) {
    if (typeof v === 'string') {
      this._choices = TosiSegmented.parseChoicesString(v)
    } else {
      this._choices = v
    }
    this.queueRender()
  }

  // Parse choices string format: value=caption:icon (caption and icon are optional)
  private static parseChoicesString(choicesStr: string): Choice[] {
    return choicesStr
      .split(',')
      .filter((c) => c.trim() !== '')
      .map((c) => {
        const [value, remains] = c.split('=').map((v) => v.trim())
        const [caption, iconName] = splitCaptionIcon(remains || value)

        const icon = iconName ? icons[iconName]() : ''
        return { value, icon, caption }
      })
  }

  value = ''

  // Form-associated lifecycle callbacks
  formDisabledCallback(disabled: boolean) {
    // Could add disabled support here
    void disabled
  }

  formResetCallback() {
    this.value = ''
  }

  get values(): string[] {
    return (this.value || '')
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v !== '')
  }

  content = () => [
    slot(),
    div({ part: 'options' }, input({ part: 'custom', hidden: true })),
  ]

  static shadowStyleSpec = {
    ':host': {
      display: 'inline-flex',
      gap: varDefault.segmentedOptionGap('8px'),
      alignItems: varDefault.segmentedAlignItems('center'),
    },
    ':host, :host::part(options)': {
      flexDirection: varDefault.segmentedDirection('row'),
    },
    ':host label': {
      display: 'inline-grid',
      alignItems: 'center',
      gap: varDefault.segmentedOptionGap('8px'),
      gridTemplateColumns:
        varDefault.segmentedOptionGridColumns('0px 24px 1fr'),
      padding: varDefault.segmentedOptionPadding('4px 12px'),
      font: varDefault.segmentedOptionFont('16px'),
    },
    ':host label:focus': {
      outline: 'none',
      boxShadow: varDefault.segmentedFocusShadow(
        `inset 0 0 0 2px ${varDefault.segmentedOptionCurrentBackground('#44a')}`
      ),
      borderRadius: varDefault.segmentedOptionsBorderRadius('8px'),
    },
    // Selection highlight is driven by an explicit `.current` class rather than
    // `label:has(:checked)`. Firefox does not reliably re-evaluate `:has(:checked)`
    // when a descendant radio's checked state changes via interaction, leaving the
    // previously-selected segment highlighted (works in Chrome/Safari).
    ':host label.current': {
      color: varDefault.segmentedOptionCurrentColor('#eee'),
      background: varDefault.segmentedOptionCurrentBackground('#44a'),
    },
    ':host label.current:focus': {
      boxShadow: varDefault.segmentedCurrentFocusShadow(
        `inset 0 0 0 2px ${varDefault.segmentedOptionCurrentColor('#eee')}`
      ),
    },
    ':host svg': {
      height: varDefault.segmentOptionIconSize('16px'),
      stroke: varDefault.segmentedOptionIconColor('currentColor'),
    },
    ':host label.no-icon': {
      gap: 0,
      gridTemplateColumns: varDefault.segmentedOptionGridColumns('0px 1fr'),
    },
    ':host input[type="radio"], :host input[type="checkbox"]': {
      visibility: varDefault.segmentedInputVisibility('hidden'),
    },
    ':host::part(options)': {
      display: 'flex',
      borderRadius: varDefault.segmentedOptionsBorderRadius('8px'),
      background: varDefault.segmentedOptionsBackground('#fff'),
      color: varDefault.segmentedOptionColor('#222'),
      overflow: 'hidden',
      alignItems: varDefault.segmentedOptionAlignItems('stretch'),
    },
    ':host::part(custom)': {
      padding: varDefault.segmentedOptionPadding('4px 12px'),
      color: varDefault.segmentedOptionCurrentColor('#eee'),
      background: varDefault.segmentedOptionCurrentBackground('#44a'),
      font: varDefault.segmentedOptionFont('16px'),
      border: '0',
      outline: 'none',
    },
    ':host::part(custom)::placeholder': {
      color: varDefault.segmentedOptionCurrentColor('#eee'),
      opacity: varDefault.segmentedPlaceholderOpacity(0.75),
    },
  }

  // Reflects the selection onto each label as a `.current` class so the highlight
  // updates reliably (see note on the `.current` style rule). Derived from
  // `this.value` (the authoritative selection, matching render()'s `checked` logic),
  // NOT from re-reading each `input.checked`: on Firefox/WebKit that live read can
  // lag the native radio update at change time, leaving `.current` on the previous
  // segment while `input.checked` (and `value`) are already correct.
  private syncCurrent() {
    const { options } = this.parts as SegmentParts
    const { values, isOtherValue } = this
    options.querySelectorAll('label').forEach((label) => {
      const val = label.querySelector('input')?.value ?? ''
      label.classList.toggle(
        'current',
        values.includes(val) || (val === '' && isOtherValue)
      )
    })
  }

  handleChange = () => {
    const { options, custom } = this.parts as SegmentParts
    if (this.multiple) {
      const inputs = [
        ...options.querySelectorAll('input:checked'),
      ] as HTMLInputElement[]
      this.value = inputs.map((input) => input.value).join(',')
    } else {
      const input = options.querySelector(
        'input:checked'
      ) as HTMLInputElement | null
      if (!input) {
        this.value = ''
      } else if (input.value) {
        custom.setAttribute('hidden', '')
        this.value = input.value
      } else {
        custom.removeAttribute('hidden')
        custom.focus()
        custom.select()
        this.value = custom.value
      }
    }
    this.syncCurrent()
  }

  handleKey = (event: KeyboardEvent) => {
    let blockEvent = false
    switch (event.code) {
      case 'Space':
        if (event.target instanceof HTMLLabelElement) {
          event.target.click()
          blockEvent = true
        }
        break
      case 'Tab':
        if (!(event.target instanceof HTMLLabelElement)) {
          const label = (event.target as HTMLElement).closest(
            'label'
          ) as HTMLLabelElement
          label.focus()
        }
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        {
          const label = (event.target as HTMLElement).closest(
            'label'
          ) as HTMLLabelElement
          if (label.previousElementSibling instanceof HTMLLabelElement) {
            label.previousElementSibling.focus!()
          }
        }
        blockEvent = true
        break
      case 'ArrowRight':
      case 'ArrowDown':
        {
          const label = (event.target as HTMLElement).closest(
            'label'
          ) as HTMLLabelElement
          if (label.nextElementSibling instanceof HTMLLabelElement) {
            label.nextElementSibling.focus!()
          }
        }
        blockEvent = true
        break
    }
    if (blockEvent) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  connectedCallback(): void {
    super.connectedCallback()

    // Parse choices from HTML attribute if present and not already set programmatically
    const choicesAttr = this.getAttribute('choices')
    if (choicesAttr && this._choices.length === 0) {
      this._choices = TosiSegmented.parseChoicesString(choicesAttr)
    }

    const { options } = this.parts

    if (this.name === '') {
      this.name = this.instanceId
    }

    options.addEventListener('change', this.handleChange)
    options.addEventListener('keydown', this.handleKey as EventListener)

    if (this.other && this.multiple) {
      console.warn(
        this,
        'is set to [other] and [multiple]; [other] will be ignored'
      )
      this.other = ''
    }
  }

  private get _choicesWithOther(): Choice[] {
    const options: Choice[] = [...this.choices]

    if (this.other && !this.multiple) {
      const [caption, icon] = splitCaptionIcon(this.other)
      options.push({
        value: '',
        caption,
        icon,
      })
    }

    return options
  }

  get isOtherValue(): boolean {
    return Boolean(
      this.value === '' ||
        (this.value &&
          !this._choicesWithOther.find((choice) => choice.value === this.value))
    )
  }

  render() {
    super.render()

    const { options, custom } = this.parts as SegmentParts
    const type = this.multiple ? 'checkbox' : 'radio'
    const { values, isOtherValue } = this
    const choices = this._choicesWithOther
    const isChecked = (value: string): boolean =>
      values.includes(value) || (value === '' && isOtherValue)

    // Idempotent: rebuild the <label>s ONLY when the choice set changed; otherwise
    // reconcile `checked` in place. Re-creating them every render (previously avoided
    // by a `valueChanged` timing flag) dropped focus from the segment you just
    // clicked and is provably redundant when the labels already match the choices —
    // so render is now safe to run any number of times, any time.
    const labels = [...options.querySelectorAll('label')] as HTMLLabelElement[]
    const structureMatches =
      labels.length === choices.length &&
      labels.every(
        (l, i) => l.querySelector('input')?.value === choices[i].value
      )

    if (structureMatches) {
      labels.forEach((label) => {
        const input = label.querySelector('input') as HTMLInputElement | null
        if (input && input.checked !== isChecked(input.value)) {
          input.checked = isChecked(input.value)
        }
      })
    } else {
      options.textContent = ''
      options.append(
        ...choices.map((choice) =>
          label(
            { tabindex: 0 },
            input({
              type,
              name: this.name,
              value: choice.value,
              checked: isChecked(choice.value),
              tabIndex: -1,
            }),
            choice.icon || { class: 'no-icon' },
            this.localized
              ? tosiLocalized(choice.caption)
              : span(choice.caption)
          )
        )
      )
    }

    if (this.other && !this.multiple) {
      custom.hidden = !isOtherValue
      const customValue = isOtherValue ? (this.value as string) : ''
      if (custom.value !== customValue) custom.value = customValue
      custom.placeholder = this.placeholder
      if (custom.parentElement !== options) options.append(custom)
    }
    this.syncCurrent()
  }
}

/** @deprecated Use TosiSegmented instead */
export type XinSegmented = TosiSegmented
/** @deprecated Use TosiSegmented instead */
export const XinSegmented: typeof TosiSegmented = TosiSegmented

export const tosiSegmented =
  TosiSegmented.elementCreator() as ElementCreator<TosiSegmented>

/** @deprecated Use tosiSegmented instead (tag is now tosi-segmented) */
export const xinSegmented = deprecated(
  (...args: Parameters<typeof tosiSegmented>) => tosiSegmented(...args),
  'xinSegmented is deprecated, use tosiSegmented instead (tag is now <tosi-segmented>)'
) as ElementCreator<TosiSegmented>
